import { CSSProperties } from "react";
import { useSelector } from "react-redux";
import { selectSequenceVariables } from "../../../app/slices/TrainingSlice";
import RichTextEditor from "../../rich_text/RichTextEditor";
import { useTrainingLocalization } from "../../trainingLocalization/TrainingLocalizationHooks";

interface SequenceLocalizedRichTextFieldProps {
  label: string;
  placeholder: string;
  localizationKey: string;
  ariaLabel?: string;
  disabled?: boolean;
  debounceTime?: number;
  minRows?: number;
  maxRows?: number;
}

const LINE_HEIGHT_PX = 20;
const VERTICAL_PADDING_PX = 16;

/**
 * Sequence node counterpart of SequenceLocalizedVariableInputField for fields
 * that are rich text in their interaction editor (Lisa message, message
 * bubble, transition). Edits the same stored format as the preview editors,
 * so formatting survives editing from the sequence.
 */
export default function SequenceLocalizedRichTextField({
  label,
  placeholder,
  localizationKey,
  ariaLabel,
  disabled = false,
  debounceTime = 500,
  minRows = 2,
  maxRows = 6,
}: SequenceLocalizedRichTextFieldProps) {
  const { changeValue, getValue } = useTrainingLocalization();
  const variables = useSelector(selectSequenceVariables);
  const rows = Math.max(1, minRows);
  const contentStyle = {
    minHeight: `${rows * LINE_HEIGHT_PX + VERTICAL_PADDING_PX}px`,
    maxHeight: `${Math.max(rows, maxRows) * LINE_HEIGHT_PX + VERTICAL_PADDING_PX}px`,
    // Variable chips match the yellow tokens of the plain sequence fields.
    "--rich-text-chip-color": "var(--pi-yellow)",
  } as CSSProperties;

  return (
    <div className="w-full noDrag nowheel">
      {label ? (
        <div className="mb-[6px] text-[14px] font-medium leading-[20px] text-creator-text-sub">
          {label}
        </div>
      ) : null}
      <RichTextEditor
        ariaLabel={ariaLabel ?? label}
        value={getValue(localizationKey)}
        onCommit={(nextValue) => changeValue(localizationKey, nextValue)}
        variables={variables}
        placeholder={placeholder}
        debounceMs={debounceTime}
        flushOnCleanupKeys={[localizationKey]}
        disabled={disabled}
        className="relative w-full"
        contentClassName={`nowheel overflow-y-auto rounded-[6px] border border-solid border-creator-border bg-creator-surface p-[8px] font-poppins text-[15px] leading-[20px] text-creator-primary transition-[border-color,box-shadow] duration-150 pit-creator-scrollbar ${
          disabled
            ? "opacity-70"
            : "hover:border-creator-text-soft focus:border-creator-primary focus:shadow-[0_0_0_3px_rgba(58,196,255,0.15)]"
        }`}
        contentStyle={contentStyle}
      />
    </div>
  );
}
